import {
	getCurrentInstance,
	ref,
	toRaw,
	watch,
} from 'vue';
import * as objectUtils from '../../../src/js/utils/object';

/**
 * Configuration hooks exposed to consumers when creating a local model binding.
 */
export interface LocalModelOptions<T> {
	clone?: (value: T) => T;
	nonImmediateEmit?: boolean;
}

/**
 * Cache environment capabilities so cloning behaviour can branch efficiently.
 */
const hasStructuredClone = typeof structuredClone === 'function';
/**
 * Cache DOM availability to avoid touching `Node` in non-browser runtimes.
 */
const hasDomNode = typeof Node !== 'undefined';

/**
 * Produce a deep clone of arbitrary reactive data while preserving DOM nodes.
 *
 * @param value Potentially reactive value to clone
 * @returns Deep cloned copy safe for mutation
 */
function cloneValue<T>(value: T): T {
	const raw = toRaw(value);

	if (
		raw === null
		|| typeof raw !== 'object'
	) {
		return raw;
	}

	const structured = tryStructuredClone(raw);

	if (structured !== null) {
		return structured;
	}

	if (Array.isArray(raw)) {
		return raw.map((item) => cloneValue(item)) as unknown as T;
	}

	if (isDomNode(raw)) {
		return raw as T;
	}

	const result: Record<PropertyKey, unknown> = {};

	for (const key of Reflect.ownKeys(raw as object)) {
		const descriptor = (
			Object.getOwnPropertyDescriptor(
				raw as object,
				key,
			)
		);

		if (
			!descriptor
			|| !('value' in descriptor)
		) {
			continue;
		}

		const nextValue = descriptor.value;

		result[key] = (
			isDomNode(nextValue)
				? nextValue
				: cloneValue(nextValue)
		);
	}

	return result as unknown as T;
}
/**
 * Type guard that recognises DOM nodes while tolerating SSR environments.
 *
 * @param value Value to test
 * @returns `true` when the value is a DOM node
 */
function isDomNode(value: unknown): value is Node {
	return (
		hasDomNode
		&& value instanceof Node
	);
}

/**
 * Attempt to clone a value via `structuredClone`, returning `null` on failure.
 *
 * @param value Input value to be cloned
 * @returns Cloned value or `null` when unsupported/erroring
 */
function tryStructuredClone<T>(value: T): T | null {
	if (!hasStructuredClone) {
		return null;
	}

	try {
		return structuredClone(value);
	} catch {
		return null;
	}
}

/**
 * Runtime helper that mirrors `v-model` behaviour using explicit emits
 * and also gives the ability to have a mutable local copy of the prop
 * to use within the component and have changes reflected even if the
 * parent does not update the prop.
 *
 * @param props Source props record supplied by the component
 * @param propName Prop key to bind locally
 * @param emit Function used to emit change events
 * @param options Optional hooks controlling cloning and emit timing
 * @returns Local ref state plus manual commit helper
 */
export function useLocalModelRuntime<
	PropType,
	PropName extends string,
>(
	props: (Record<PropName, PropType> | undefined),
	propName: PropName,
	emit: (event: string, payload: PropType) => void,
	options?: LocalModelOptions<PropType>,
) {
	const clone = (
		options?.clone
		?? ((v: PropType) => cloneValue(v))
	);
	const updateEvent = `update:${propName}`;
	const instance = getCurrentInstance();
	const resolvedProps = (
		props
		?? (instance?.props as Record<PropName, PropType> | undefined)
		?? (instance?.proxy?.$props as Record<PropName, PropType> | undefined)
	);

	if (!resolvedProps) {
		throw new Error('[vue-local-model] Unable to resolve component props for useLocalModelRuntime.');
	}

	const state = ref<PropType>(clone(resolvedProps[propName]));

	/**
	 * React to upstream prop changes and keep the local state in sync.
	 */
	watch(
		() => resolvedProps[propName],
		(
			newValue,
			oldValue,
		) => {
			const clonedValue = clone(newValue);

			if (typeof newValue === 'undefined') {
				state.value = undefined;
			} else if (typeof oldValue === 'undefined') {
				state.value = clonedValue;
			} else if (
				clonedValue !== null
				&& typeof clonedValue === 'object'
				&& state.value !== null
				&& typeof state.value === 'object'
			) {
				if (
					Array.isArray(clonedValue)
					&& Array.isArray(state.value as unknown[])
				) {
					const target = state.value as unknown[];
					target.splice(
						0,
						target.length,
						...clonedValue,
					);
				} else {
					const objectDifferences = objectUtils.getObjectDifferences(
						state.value as Record<PropertyKey, unknown>,
						clonedValue as Record<PropertyKey, unknown>,
					);

					for (const objectDifference of objectDifferences) {
						if (
							!(objectDifference in clonedValue)
							&& objectDifference in state.value
						) {
							delete state.value[objectDifference];
						} else {
							// @ts-expect-error: Key is known to exist here
							state.value[objectDifference as keyof PropType] = clonedValue[objectDifference];
						}
					}
				}
			} else if (state.value !== clonedValue) {
				state.value = clonedValue;
			}
		},
		{
			deep: true,
		},
	);

	/**
	 * Emit updates whenever local edits occur, unless deferred by configuration.
	 */
	watch(
		() => state.value,
		(newValue) => {
			if (!options?.nonImmediateEmit) {
				commit(newValue);
			}
		},
		{
			deep: true,
		},
	);

	/**
	 * Emit the current state value back to the parent component.
	 *
	 * @param newValue Optional pre-cloned payload to emit instead of `state.value`
	 */
	function commit(newValue = state.value) {
		emit?.(
			updateEvent,
			toRaw(newValue),
		);
	}

	return {
		state,
		commit,
		prop: resolvedProps[propName],
	};
}
